"use client";

import { useState, useEffect, useRef, useCallback, Suspense } from "react";
import { useRouter } from "next/navigation";
import { X } from "lucide-react";
import { FiTrendingUp, FiUsers, FiFileText, FiMessageSquare, FiCommand, FiSearch, FiClock } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { SearchIcon } from "./icons/SearchIcon";
import GradientAvatar from "./GradientAvatar";
import { trackRecommendation } from "@/lib/recommendation-tracker";
import { sourceUrl } from "@/lib/source-url";
import { useCurrentSource } from "@/hooks/useCurrentSource";
import { authFetch } from "@/lib/authFetch";
import { useT } from "@/hooks/useT";

interface SearchResult {
  type: "market" | "event" | "trader" | "blog" | "talk" | "user" | "category";
  id: string;
  title: string;
  subtitle?: string;
  image?: string;
  href: string;
  score?: number;
  source?: string; // polymarket | kalshi | predict
  /** Pre-formatted volume + liquidity strings. When present the
   *  market/event row shows them as compact chips next to the title
   *  instead of the plain `subtitle` line. */
  volumeUsd?: number;
  liquidityUsd?: number;
}

// Section config: order, max items per section. `labelKey` resolves
// against the i18n catalog at render — `label` is the EN fallback so
// the existing UI keeps working if the key is ever missing.
const SECTIONS: { key: SearchResult["type"]; label: string; labelKey: string; max: number }[] = [
  { key: "category", label: "Categories", labelKey: "search.section.categories", max: 3 },
  { key: "market", label: "Markets", labelKey: "search.section.markets", max: 6 },
  { key: "event", label: "Events", labelKey: "search.section.events", max: 6 },
  { key: "trader", label: "Traders", labelKey: "search.section.traders", max: 3 },
  { key: "user", label: "Users", labelKey: "search.section.users", max: 2 },
  { key: "talk", label: "Posts", labelKey: "search.section.posts", max: 2 },
  { key: "blog", label: "Blog", labelKey: "search.section.blog", max: 2 },
];

// Compact USD formatter for the volume/liquidity badges. Module-scope
// so both the search-merge logic and the row renderer can reach it
// without duplicating the breakpoints (1k / 1M).
function fmtUsd(value: number, suffix: "vol" | "liq") {
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M ${suffix}`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K ${suffix}`;
  return `$${value.toFixed(0)} ${suffix}`;
}

// Recent searches in localStorage. Stores the QUERY STRINGS the user
// typed (not the clicked results) so reopening the dropdown surfaces
// "things I've searched for" — clicking a recent re-runs the search,
// which matches the intent users actually express ("show me my market
// list again") instead of teleporting to a single past result.
const RECENT_STORAGE_KEY = "frenflow:recent-queries";
const RECENT_MAX = 8;

function loadRecentQueries(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((q) => typeof q === "string" && q.trim().length > 0).slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function saveRecentQuery(query: string) {
  if (typeof window === "undefined") return;
  const q = query.trim();
  if (!q || q.length < 2) return;
  try {
    const current = loadRecentQueries();
    // Move-to-front, case-insensitive dedup.
    const lower = q.toLowerCase();
    const next = [q, ...current.filter((r) => r.toLowerCase() !== lower)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode — silent no-op */
  }
}

function clearRecentQueries() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(RECENT_STORAGE_KEY);
  } catch {
    /* silent */
  }
}

function GlobalSearchContent() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recents, setRecents] = useState<string[]>([]);

  // Hydrate recent searches once on mount. localStorage isn't available
  // during SSR so we defer to the effect rather than seeding useState.
  useEffect(() => {
    setRecents(loadRecentQueries());
  }, []);

  // Warm up the image CDNs that result thumbnails come from so the
  // 40×40 previews paint within the first frame after results land,
  // instead of a 200-400ms TLS+CDN spike per host. The hints are
  // idempotent — multiple mounts (e.g. modal reopen) are no-ops in
  // the browser.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const hosts = [
      "https://polymarket-upload.s3.us-east-2.amazonaws.com",
      "https://kalshi-public-docs.s3.amazonaws.com",
      "https://images.frenflow.com",
    ];
    const added: HTMLLinkElement[] = [];
    for (const href of hosts) {
      if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) continue;
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = href;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
      added.push(link);
    }
    return () => { added.forEach((l) => l.remove()); };
  }, []);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Per-session result cache keyed by `${currentSource}::${query}`. Avoids
  // re-hitting the backend when the user deletes a char and retypes the
  // same prefix, or reopens the search dropdown within the same session.
  // 60s TTL is enough for prediction markets — vol/liq stats don't change
  // fast enough to matter for a search dropdown.
  const cacheRef = useRef<Map<string, { results: SearchResult[]; ts: number }>>(new Map());
  const CACHE_TTL_MS = 60_000;
  const router = useRouter();
  const currentSource = useCurrentSource();
  const t = useT();

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Flatten grouped results for keyboard navigation
  const flatResults = results;

  const handleKeyNavigation = useCallback((e: React.KeyboardEvent) => {
    if (!flatResults.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % flatResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + flatResults.length) % flatResults.length);
    } else if (e.key === "Enter" && flatResults[selectedIndex]) {
      e.preventDefault();
      handleResultClick(flatResults[selectedIndex]);
    }
  }, [flatResults, selectedIndex]);

  useEffect(() => { setSelectedIndex(0); }, [results]);

  // ── Progressive search with AbortController + 150ms debounce + LRU cache ──
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed || trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    // Cache hit — paint instantly, skip the network entirely. The cache
    // key includes currentSource because the source filter changes which
    // results survive `mergeResults`, so the same query under a different
    // selector legitimately produces a different result set.
    const cacheKey = `${currentSource || ""}::${trimmed.toLowerCase()}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setResults(cached.results);
      setLoading(false);
      return;
    }

    // Decode HTML entities (&quot; &amp; etc.)
    const decodeHtml = (s: string) => s.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#39;/g, "'").replace(/&apos;/g, "'");

    // State the cleanup needs to reach into — defined ahead of the
    // debounced runSearch so the returned cleanup can abort whatever
    // got scheduled regardless of which phase we're in.
    let activeController: AbortController | null = null;
    let spinnerTimer: ReturnType<typeof setTimeout> | null = null;

    const runSearch = () => {
    // Abort previous search cycle
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    activeController = controller;
    const signal = controller.signal;

    setLoading(true);

    const encodedQuery = encodeURIComponent(query.trim());
    const isKalshi = currentSource === "kalshi";
    const isPredict = currentSource === "predict";
    const seenIds = new Set<string>();
    let merged: SearchResult[] = [];
    // Race guard: until the active-source fetch returns (or fails) we
    // keep other-source results hidden so they don't flash in first.
    // Without this, Kalshi/Predict typically land before Polymarket and
    // the dropdown briefly shows the wrong source before the filter
    // kicks in, which reads as "the search ignores my selector".
    let currentSourceFetchDone = currentSource ? false : true;

    const fmtVol = (vol: number) =>
      vol >= 1_000_000 ? `$${(vol / 1_000_000).toFixed(1)}M vol`
      : vol >= 1_000 ? `$${(vol / 1_000).toFixed(0)}K vol`
      : vol > 0 ? `$${vol} vol` : undefined;

    // Helper: merge new results, dedup, sort, update state.
    // Market/event rows are scoped to `currentSource` when there's at
    // least one match from that source — so users browsing under the
    // Polymarket selector don't get Kalshi noise polluting their picks.
    // Falls back to cross-source results when the active source returns
    // nothing for that query (e.g. Kalshi-only "elections" markets while
    // sitting on Polymarket). Traders/categories are out of scope here.
    const mergeResults = (newItems: SearchResult[]) => {
      if (signal.aborted) return;
      for (const item of newItems) {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          merged.push(item);
        }
      }
      const marketEventItems = merged.filter(r => r.type === "market" || r.type === "event");
      const otherItems = merged.filter(r => r.type !== "market" && r.type !== "event");
      const hasCurrentSource = currentSource && marketEventItems.some(r => r.source === currentSource);
      let scopedMarketsAndEvents: SearchResult[];
      if (hasCurrentSource) {
        // Strict scope: current source has results, hide others entirely.
        scopedMarketsAndEvents = marketEventItems.filter(r => r.source === currentSource);
      } else if (currentSource && !currentSourceFetchDone) {
        // Active-source fetch hasn't landed yet — withhold other-source
        // rows to avoid the visible flash, even if we have nothing else
        // to render meanwhile. Spinner stays visible.
        scopedMarketsAndEvents = [];
      } else {
        // No current source, or its fetch returned 0 results — fall
        // through and show whatever we have across sources.
        scopedMarketsAndEvents = marketEventItems;
      }
      const sorted = [...scopedMarketsAndEvents, ...otherItems].sort((a, b) => (b.score || 0) - (a.score || 0));
      const limited: SearchResult[] = [];
      const counts: Record<string, number> = {};
      for (const item of sorted) {
        const section = SECTIONS.find(s => s.key === item.type) || SECTIONS.find(s => s.key === "market")!;
        const count = counts[section.key] || 0;
        if (count < section.max) {
          limited.push(item);
          counts[section.key] = count + 1;
        }
      }
      setResults(limited);
      // Persist the query as a recent search the first time results
      // land for it. Tracks what the user looked for, not which row
      // they clicked — clicks already get separate handling.
      if (limited.length > 0) {
        saveRecentQuery(trimmed);
        setRecents(loadRecentQueries());
      }
      // Save to cache. Last writer wins — every mergeResults call
      // updates the cache so the freshest snapshot survives even when
      // multiple fetches land out of order.
      cacheRef.current.set(cacheKey, { results: limited, ts: Date.now() });
      // Cap the cache at 50 entries — LRU-ish: oldest insertion order.
      // Search dropdowns don't need a proper LRU; this is just a leak
      // guard for users who hammer the box.
      if (cacheRef.current.size > 50) {
        const firstKey = cacheRef.current.keys().next().value;
        if (firstKey) cacheRef.current.delete(firstKey);
      }
    };

    // Fire all searches independently — each updates UI when ready
    const safeFetch = (url: string, usAuth = false) =>
      (usAuth ? authFetch(url, { signal }) : fetch(url, { signal }))
        .then(r => r.ok ? r.json() : null)
        .catch(() => null);

    // 1. Polymarket (primary, Predexon-ranked) — handles both Gamma and Predexon formats
    safeFetch(`/api/polymarket/search?q=${encodedQuery}&limit=12`).then(data => {
      if (!currentSource || currentSource === "polymarket") currentSourceFetchDone = true;
      if (!data) {
        // Still nudge mergeResults so the fallback path can unlock when
        // the active source returned nothing.
        if (currentSource === "polymarket") mergeResults([]);
        return;
      }
      const markets = data.results || data.events || data || [];
      const items: SearchResult[] = [];
      const seenEventSlugs = new Set<string>();

      (Array.isArray(markets) ? markets : []).forEach((item: any) => {
        const market = item.market || item;
        if (!market) return;
        const img = market.image || market.image_url || market.icon;
        const vol = market.volume || market.total_volume_usd || 0;
        const liqRaw = market.liquidity ?? market.liquidity_usd ?? market.liquidityNum ?? 0;
        const liq = typeof liqRaw === "string" ? parseFloat(liqRaw) : Number(liqRaw);
        const volNum = typeof vol === "string" ? parseFloat(vol) : Number(vol);
        const isGammaEvent = Array.isArray(market.markets) && market.markets.length > 0;
        const marketSlug = market.market_slug || market.slug || market.condition_id || market.conditionId;
        const eventSlug = market.event_slug || market.slug;
        const id = market.market_id || market.id || market.condition_id || market.conditionId || marketSlug;
        const baseScore = isKalshi || isPredict ? 0.85 : (item.score || 1);

        // Gamma multi-outcome events
        if (isGammaEvent) {
          items.push({
            type: "event", id, source: "polymarket", image: img, score: baseScore,
            title: decodeHtml(market.question || market.title || "Unknown Event"),
            volumeUsd: volNum > 0 ? volNum : undefined,
            liquidityUsd: liq > 0 ? liq : undefined,
            href: sourceUrl(`/event/${eventSlug}`, "polymarket"),
          });
          return;
        }

        // Predexon/Gamma individual market → add as market.
        // When the market lives inside a multi-outcome event we route to
        // the event page with `?market=<slug>` so the picked outcome
        // opens already selected/expanded inside the event view, instead
        // of the bare /market/<slug> page (less context, no sibling
        // outcomes). EventClient already reads the query param.
        const marketHref =
          market.event_slug && market.event_slug !== marketSlug
            ? `/event/${market.event_slug}?market=${marketSlug}`
            : `/market/${marketSlug}`;
        items.push({
          type: "market", id, source: "polymarket", image: img, score: baseScore,
          title: decodeHtml(market.question || market.title || "Unknown Market"),
          volumeUsd: volNum > 0 ? volNum : undefined,
          liquidityUsd: liq > 0 ? liq : undefined,
          href: sourceUrl(marketHref, "polymarket"),
        });

        // Also add the parent event (deduped) — only if event_title differs from market title (multi-outcome)
        const mTitle = (market.question || market.title || "").toLowerCase().trim();
        const eTitle = (market.event_title || "").toLowerCase().trim();
        if (market.event_slug && market.event_title && eTitle !== mTitle && !seenEventSlugs.has(market.event_slug)) {
          seenEventSlugs.add(market.event_slug);
          items.push({
            type: "event", id: `evt-${market.event_slug}`, source: "polymarket", image: img, score: baseScore * 0.95,
            title: decodeHtml(market.event_title),
            volumeUsd: volNum > 0 ? volNum : undefined,
            liquidityUsd: liq > 0 ? liq : undefined,
            href: sourceUrl(`/event/${market.event_slug}`, "polymarket"),
          });
        }
      });
      mergeResults(items);
    });

    // 2. Kalshi
    safeFetch(`/api/markets/kalshi/text-search?q=${encodedQuery}&limit=10`).then(data => {
      if (currentSource === "kalshi") currentSourceFetchDone = true;
      if (!data?.results) {
        if (currentSource === "kalshi") mergeResults([]);
        return;
      }
      const items: SearchResult[] = data.results.map((m: any) => ({
        type: m.marketsCount > 1 ? "event" as const : "market" as const,
        id: `kalshi-${m.id}`,
        title: m.title,
        // Kalshi exposes dollar_volume (USD); plain `volume` is contract
        // count and would read as 100x lower than the equivalent
        // Polymarket row. open_interest_usd ≈ liquidity for binary
        // markets (no orderbook depth concept on Kalshi).
        volumeUsd: Number(m.dollar_volume ?? m.volume ?? 0) || undefined,
        liquidityUsd: Number(m.open_interest_usd ?? m.liquidity ?? 0) || undefined,
        subtitle: !m.dollar_volume && !m.volume ? m.category : undefined,
        image: m.image || undefined,
        href: sourceUrl(`/event/kalshi/${m.slug?.toUpperCase() || m.id.replace("ks-", "")}`, "kalshi"),
        score: isKalshi ? 1 : 0.8,
        source: "kalshi",
      }));
      mergeResults(items);
    });

    // 3. Predict.fun
    safeFetch(`/api/markets/predict/search?q=${encodedQuery}&limit=5`).then(data => {
      if (currentSource === "predict") currentSourceFetchDone = true;
      if (!data?.markets) {
        if (currentSource === "predict") mergeResults([]);
        return;
      }
      const items: SearchResult[] = (Array.isArray(data.markets) ? data.markets : []).map((m: any) => ({
        type: "market" as const,
        id: `predict-${m.id || m.slug}`,
        title: m.title || m.question || "Unknown",
        volumeUsd: Number(m.volume ?? m.volume24hr ?? 0) || undefined,
        liquidityUsd: Number(m.liquidity ?? m.liquidityUsd ?? 0) || undefined,
        image: m.imageUrl || m.image || undefined,
        href: `/predictfun/event/${m.slug || m.id}`,
        score: isPredict ? 1 : 0.75,
        source: "predict",
      }));
      mergeResults(items);
    });

    // 4. Traders
    // Wallet-shaped queries (0x + hex) only ever match the polymarket /
    // predict tables — kalshi_traders stores nicknames, not addresses. So
    // when the user pastes a wallet while browsing under a Kalshi-source
    // page, drop the source filter so the backend falls through to the
    // polymarket_users path. Otherwise we'd return 0 traders even when
    // the wallet clearly exists in our index (the marcnietobrand /
    // 0xa3B96F42... case the user reported).
    const isWalletQuery = /^0x[0-9a-fA-F]+$/.test(query.trim());
    const traderSource = isWalletQuery
      ? ""
      : isKalshi ? "&source=kalshi" : isPredict ? "&source=predict" : "";
    safeFetch(`/api/traders/search?q=${encodedQuery}&limit=4${traderSource}`).then(data => {
      if (!data?.traders) return;
      const items: SearchResult[] = data.traders.map((t: any) => {
        // When the trader corresponds to a FrenFlow user (matched by
        // wallet/safe/deposit address in the backend join), surface the
        // FrenFlow handle + avatar rather than the truncated PM
        // pseudonym. The trader profile page already does this — the
        // search dropdown should match.
        const ffTitle = t.isFrenFlowUser ? (t.frenFlowUsername || t.frenFlowName) : null;
        const ffAvatar = t.isFrenFlowUser ? t.frenFlowAvatar : null;
        return {
          type: "trader" as const,
          id: t.id || t.address,
          title: ffTitle || t.username || t.name || `${t.address?.slice(0, 6)}...${t.address?.slice(-4)}`,
          subtitle: t.totalVolume ? `$${(parseFloat(t.totalVolume) / 1_000_000).toFixed(2)}M vol` :
                   t.totalPnL ? `$${parseFloat(t.totalPnL).toFixed(0)} PnL` : undefined,
          image: ffAvatar || t.avatar || t.profileImage,
          href: `/traders/${t.address}`,
          score: 0.9,
        };
      });
      mergeResults(items);
    });

    // 5. FrenFlow users
    safeFetch(`/api/social/users/search?q=${encodedQuery}&limit=3`, true).then(data => {
      if (!data?.users) return;
      const items: SearchResult[] = data.users.map((u: any) => ({
        type: "user" as const,
        id: u.id,
        title: u.name || u.username,
        subtitle: u.username ? `@${u.username}` : undefined,
        image: u.image,
        href: `/profile/${u.username}`,
        score: 0.85,
      }));
      mergeResults(items);
    });

    // 6. Posts/talks
    safeFetch(`/api/social/posts/search?q=${encodedQuery}&limit=3`, true).then(data => {
      if (!data?.posts) return;
      const items: SearchResult[] = data.posts.map((p: any) => ({
        type: "talk" as const,
        id: p.id,
        title: decodeHtml((p.content || "").slice(0, 80) + (p.content?.length > 80 ? "..." : "")),
        subtitle: `by @${p.username}`,
        image: p.user_image,
        href: `/post/${p.id}`,
        score: 0.6,
      }));
      mergeResults(items);
    });

    // 7. Blog (lightweight — just search endpoint, no fetch-all). The
    // route is `/api/blog/posts`, not `/api/blog` — the bare path 404s
    // and the catch silenced it but the browser still logged the miss.
    safeFetch(`/api/blog/posts?limit=50`).then(data => {
      if (!data?.posts) return;
      const lq = query.toLowerCase();
      const items: SearchResult[] = [];
      for (const post of data.posts) {
        if (post.title?.toLowerCase().includes(lq) || post.excerpt?.toLowerCase().includes(lq)) {
          items.push({
            type: "blog",
            id: post.slug,
            title: post.title,
            subtitle: post.date,
            image: post.coverImage,
            href: `/blog/${post.slug}`,
            score: 0.7,
          });
          if (items.length >= 2) break;
        }
      }
      mergeResults(items);
    });

    // Categories search removed (2026-05-13). The local label map and the
    // dynamic taxonomy fallback both built hrefs like
    // `/polymarket/markets/trump` — these category routes were never
    // shipped, so every click landed on a 404. The right surface for
    // "browse a category" is the markets page sidebar, not the search
    // dropdown. Markets/events/traders are still searched below.

    // After a reasonable time, stop showing spinner
    spinnerTimer = setTimeout(() => {
      if (!signal.aborted) setLoading(false);
    }, 900);
    };

    // 150ms debounce window — avoids firing a fetch on every keystroke
    // while still feeling instant to the user. Cache lookups happen
    // synchronously above, so a repeated query lands in <1ms anyway.
    const debounceTimer = setTimeout(runSearch, 150);

    return () => {
      clearTimeout(debounceTimer);
      if (spinnerTimer) clearTimeout(spinnerTimer);
      activeController?.abort();
    };
  }, [query, currentSource]);

  const handleResultClick = (result: SearchResult) => {
    // Close immediately — before navigation starts
    setIsOpen(false);
    setQuery("");
    setResults([]);
    inputRef.current?.blur();
    if (result.type === "market" || result.type === "event") {
      trackRecommendation(result.id, "search_click", "search");
    }
    // Persist the active query (not the clicked result) as a recent —
    // matches the "things I searched for" mental model, so reopening
    // the dropdown surfaces the search terms not specific markets.
    if (query.trim().length >= 2) {
      saveRecentQuery(query);
      setRecents(loadRecentQueries());
    }
    router.push(result.href);
  };

  const handleClearRecents = () => {
    clearRecentQueries();
    setRecents([]);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "market":
      case "event":
        return <FiTrendingUp className="w-4 h-4" />;
      case "trader":
      case "user":
        return <FiUsers className="w-4 h-4" />;
      case "blog":
        return <FiFileText className="w-4 h-4" />;
      case "talk":
        return <FiMessageSquare className="w-4 h-4" />;
      case "category":
        return <FiSearch className="w-4 h-4" />;
      default:
        return <FiSearch className="w-4 h-4" />;
    }
  };

  // Source badge config — byte-identical to what SourceSelector renders
  // on its mobile/icon-only chip: solid brand colour, white logo (or
  // "P" letter for Predict since the brand mark isn't ready). Same
  // tokens so the two surfaces stay aligned if the brand changes.
  const SOURCE_CONFIG: Record<string, { label: string; color: string; logoBg?: string; logo: string; letter?: string }> = {
    polymarket: { label: "Polymarket", color: "#2E5CFF", logo: "/logos/polymarket/icon-white.png" },
    kalshi: { label: "Kalshi", color: "#09C285", logo: "/logos/kalshi/k-icon.svg" },
    hyperliquid: { label: "Hyperliquid", color: "#97FCE4", logoBg: "#0f1a1f", logo: "/logos/hyperliquid/symbol-mint.svg" },
    predict: { label: "Predict.fun", color: "#6366F1", logo: "", letter: "P" },
  };

  const getSourceLogoOverlay = (source?: string) => {
    if (!source) return null;
    const cfg = SOURCE_CONFIG[source];
    if (!cfg) return null;
    return (
      <div
        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-[4px] flex items-center justify-center ring-1 ring-white dark:ring-[#18181D]"
        style={{ backgroundColor: cfg.logoBg ?? cfg.color }}
      >
        {cfg.logo ? (
          <img src={cfg.logo} alt={cfg.label} className="w-2.5 h-2.5 object-contain" />
        ) : (
          <span className="text-[8px] font-black text-black leading-none">{cfg.letter}</span>
        )}
      </div>
    );
  };

  // Group results by section for display
  const groupedSections = SECTIONS
    .map(section => ({
      ...section,
      items: flatResults.filter(r => r.type === section.key),
    }))
    .filter(s => s.items.length > 0);

  // Flat index for keyboard nav
  let flatIndex = 0;
  const getFlatIndex = () => flatIndex++;

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl">
      {/* Search Input */}
      <div className="relative group">
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 group-focus-within:text-gray-600 dark:group-focus-within:text-gray-300 transition-colors">
          <SearchIcon size={16} />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyNavigation}
          placeholder={t("search.placeholder")}
          className="w-full h-9 pl-10 pr-16 bg-black/[0.03] dark:bg-white/[0.05] rounded-[5px] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 font-satoshi transition-colors focus:outline-none focus:bg-black/[0.06] dark:focus:bg-white/[0.08] focus:ring-1 focus:ring-black/[0.08] dark:focus:ring-white/[0.1]"
        />
        <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading ? (
            <div className="w-3.5 h-3.5 border-[1.5px] border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
          ) : query ? (
            <button
              onClick={() => { setQuery(""); setResults([]); }}
              className="p-0.5 rounded-[5px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : (
            <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-black/[0.04] dark:bg-white/[0.06] rounded">
              <FiCommand className="w-2.5 h-2.5" />K
            </kbd>
          )}
        </div>
      </div>

      {/* Recent searches — shown when the dropdown is open with an
          empty query and we've stored at least one click. Same look as
          the trader/user list rows so users don't have to re-orient. */}
      <AnimatePresence>
        {isOpen && query.length < 2 && recents.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.12 }}
            className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] sm:w-full lg:w-[720px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-white/[0.08] rounded-[5px] shadow-2xl overflow-hidden z-50"
          >
            <div className="max-h-[60vh] lg:max-h-[480px] overflow-y-auto scrollbar-hide p-2">
              <div className="flex items-center justify-between px-3 pt-1 pb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {t("search.section.recent") || "Recent"}
                </span>
                <button
                  type="button"
                  onClick={handleClearRecents}
                  className="text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  {t("search.section.clear") || "Clear"}
                </button>
              </div>
              <div className="space-y-0.5">
                {recents.map((q) => (
                  <button
                    key={`recent-${q}`}
                    onClick={() => {
                      // Re-run the search for this term. setQuery flips
                      // the dropdown into results mode via the search
                      // effect (cache hit if recent, debounce + fetch
                      // otherwise).
                      setQuery(q);
                      inputRef.current?.focus();
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-[5px] text-left hover:bg-black/[0.04] dark:hover:bg-white/[0.05] transition-colors"
                  >
                    <div className="w-7 h-7 rounded-[5px] bg-gray-100 dark:bg-white/[0.06] flex-shrink-0 flex items-center justify-center text-gray-400 dark:text-gray-500">
                      <FiClock className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 dark:text-white truncate" title={q}>{q}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results Dropdown — command palette style */}
      <AnimatePresence>
        {isOpen && query.length >= 2 && (results.length > 0 || loading) && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.12 }}
            className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] sm:w-full lg:w-[720px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-white/[0.08] rounded-[5px] shadow-2xl overflow-hidden z-50"
          >
            <div className="max-h-[60vh] lg:max-h-[480px] overflow-y-auto scrollbar-hide p-2">
              {/* Categories — horizontal chips at top */}
              {groupedSections.find(s => s.key === "category") && (
                <div className="px-2 pt-1 pb-2">
                  <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
                    {groupedSections.find(s => s.key === "category")!.items.map((result) => {
                      const idx = getFlatIndex();
                      return (
                        <button
                          key={result.id}
                          onClick={() => handleResultClick(result)}
                          onMouseEnter={() => { setSelectedIndex(idx); router.prefetch(result.href); }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                            idx === selectedIndex
                              ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                              : "bg-black/[0.04] dark:bg-white/[0.06] text-gray-700 dark:text-gray-300 hover:bg-black/[0.12] dark:hover:bg-white/[0.14]"
                          }`}
                        >
                          <FiSearch className="w-3 h-3" />
                          {result.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Markets + Events — 2-col grid on desktop. The section
                  cap caps the dropdown at a glanceable size; deeper
                  exploration goes through the "View all results" link
                  rendered after the last market/event section, which
                  routes to /<source>/markets?q=… (full filterable
                  results page). */}
              {groupedSections.filter(s => s.key === "market" || s.key === "event").map((section) => (
                <div key={section.key} className="px-2 pb-2">
                  <div className="flex items-center gap-2 px-1 pt-1 pb-1.5">
                    <span className="text-[10px] font-semibold text-gray-900 dark:text-white uppercase tracking-wider">{t(section.labelKey)} <span className="text-gray-400 dark:text-gray-500">({section.items.length})</span></span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5">
                    {section.items.map((result) => {
                      const idx = getFlatIndex();
                      return (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          onMouseEnter={() => { setSelectedIndex(idx); router.prefetch(result.href); }}
                          className={`flex items-center gap-3 px-2.5 py-2 rounded-[5px] text-left transition-colors ${
                            idx === selectedIndex
                              ? "bg-gray-50 dark:bg-white/[0.06]"
                              : "hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                          }`}
                        >
                          <div className="flex-shrink-0 relative">
                            {result.image ? (
                              <div className="relative w-10 h-10 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-white/[0.06]">
                                <Image src={result.image} alt={result.title} fill className="object-cover" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center text-gray-400 dark:text-gray-500">
                                {getIcon(result.type)}
                              </div>
                            )}
                            {getSourceLogoOverlay(result.source)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-gray-900 dark:text-white truncate" title={result.title}>{result.title}</div>
                            {(result.volumeUsd != null || result.liquidityUsd != null || result.subtitle) && (
                              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                {result.volumeUsd != null && (
                                  <span className="inline-flex items-center px-1.5 py-px rounded-[4px] text-[10px] tabular-nums bg-gray-50 dark:bg-white/[0.03] text-gray-500 dark:text-gray-500">
                                    {fmtUsd(result.volumeUsd, "vol")}
                                  </span>
                                )}
                                {result.liquidityUsd != null && (
                                  <span className="inline-flex items-center px-1.5 py-px rounded-[4px] text-[10px] tabular-nums bg-gray-50 dark:bg-white/[0.03] text-gray-500 dark:text-gray-500">
                                    {fmtUsd(result.liquidityUsd, "liq")}
                                  </span>
                                )}
                                {result.subtitle && result.volumeUsd == null && result.liquidityUsd == null && (
                                  <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{result.subtitle}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* View all results — only shown when there's a market or
                  event in the dropdown, so users with a typo or trader-
                  only query don't get pushed to an empty markets page.
                  Source-aware: lands on /<source>/markets?q=… so the
                  full filterable results grid is one click away. */}
              {groupedSections.some(s => s.key === "market" || s.key === "event") && query.trim().length >= 2 && (() => {
                // Dedicated search page (source-aware via useCurrentSource
                // on the client). Mirrors Polymarket's /predictions/[q]
                // pattern — gives users a "full results" page instead of
                // an inline scroll inside the dropdown.
                const href = `/search/${encodeURIComponent(query.trim())}`;
                return (
                  <div className="px-2 pb-2 pt-1 border-t border-gray-100 dark:border-white/[0.04] flex justify-center">
                    <Link
                      href={href}
                      onClick={() => { setIsOpen(false); inputRef.current?.blur(); }}
                      className="inline-flex items-center px-4 py-2 rounded-[5px] text-[12px] font-semibold bg-white text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      <span>{t("search.viewAll") || "View all results for"} &ldquo;{query.trim()}&rdquo;</span>
                    </Link>
                  </div>
                );
              })()}

              {/* Traders — horizontal scroll cards */}
              {groupedSections.find(s => s.key === "trader") && (
                <div className="px-2 pb-2">
                  <div className="flex items-center gap-2 px-1 pt-1 pb-1.5">
                    <span className="text-[10px] font-semibold text-gray-900 dark:text-white uppercase tracking-wider">{t("search.section.traders")} <span className="text-gray-400 dark:text-gray-500">({groupedSections.find(s => s.key === "trader")!.items.length})</span></span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5">
                    {groupedSections.find(s => s.key === "trader")!.items.map((result) => {
                      const idx = getFlatIndex();
                      return (
                        <button
                          key={result.id}
                          onClick={() => handleResultClick(result)}
                          onMouseEnter={() => { setSelectedIndex(idx); router.prefetch(result.href); }}
                          className={`flex-shrink-0 w-36 flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-[5px] text-center transition-colors ${
                            idx === selectedIndex
                              ? "bg-gray-50 dark:bg-white/[0.06]"
                              : "hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                          }`}
                        >
                          {result.image ? (
                            <div className="relative w-10 h-10 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-white/[0.06]">
                              <Image src={result.image} alt={result.title} fill className="object-cover" />
                            </div>
                          ) : (
                            // Seeded gradient avatar — matches the trader
                            // profile + activity rows so the same wallet
                            // reads as the same person across surfaces.
                            // Use the wallet id when present, fall back
                            // to the title.
                            <GradientAvatar seed={result.id || result.title} size={40} className="!rounded-[5px]" />
                          )}
                          <div className="w-full">
                            <div className="text-xs font-semibold text-gray-900 dark:text-white truncate" title={result.title}>{result.title}</div>
                            {result.subtitle && <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{result.subtitle}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Users, Posts, Blog — compact list */}
              {groupedSections.filter(s => s.key === "user" || s.key === "talk" || s.key === "blog").map((section) => (
                <div key={section.key} className="px-2 pb-1">
                  <div className="flex items-center gap-2 px-1 pt-1 pb-1">
                    <span className="text-[10px] font-semibold text-gray-900 dark:text-white uppercase tracking-wider">{t(section.labelKey)} <span className="text-gray-400 dark:text-gray-500">({section.items.length})</span></span>
                  </div>
                  {section.items.map((result) => {
                    const idx = getFlatIndex();
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        onMouseEnter={() => { setSelectedIndex(idx); router.prefetch(result.href); }}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-[5px] text-left transition-colors ${
                          idx === selectedIndex
                            ? "bg-gray-50 dark:bg-white/[0.06]"
                            : "hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                        }`}
                      >
                        {result.image ? (
                          <div className="relative w-7 h-7 rounded-[5px] overflow-hidden bg-gray-100 dark:bg-white/[0.06] flex-shrink-0">
                            <Image src={result.image} alt={result.title} fill className="object-cover" />
                          </div>
                        ) : (
                          <div className="w-7 h-7 rounded-[5px] bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center text-gray-400 dark:text-gray-500 flex-shrink-0">
                            {getIcon(result.type)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-gray-900 dark:text-white truncate" title={result.title}>{result.title}</div>
                          {result.subtitle && <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{result.subtitle}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}

              {loading && results.length > 0 && (
                <div className="px-3.5 py-2 text-center">
                  <div className="w-3 h-3 border-[1.5px] border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              )}
            </div>
          </motion.div>
        )}

        {isOpen && query.length >= 2 && !loading && results.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full mt-2 w-full bg-white dark:bg-[#18181D] border border-gray-200 dark:border-white/[0.08] rounded-[5px] shadow-lg p-6 text-center z-50"
          >
            <FiSearch className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("search.noResultsFor")}{" "}
              "<span className="font-semibold text-gray-700 dark:text-gray-300">{query}</span>"
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function GlobalSearch() {
  return (
    <Suspense fallback={<div className="w-full h-[38px]" />}>
      <GlobalSearchContent />
    </Suspense>
  );
}
